import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const STATUS_CONFIG = {
  current:     { emoji: '🟢', label: 'Current',     color: '#16a34a', bg: '#dcfce7', border: '#86efac' },
  promoted:    { emoji: '✅', label: 'Promoted',    color: '#1d4ed8', bg: '#dbeafe', border: '#93c5fd' },
  failed:      { emoji: '❌', label: 'Failed',      color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
  transferred: { emoji: '🔄', label: 'Transferred', color: '#d97706', bg: '#fef3c7', border: '#fcd34d' },
};

function AttendanceBadge({ summary }) {
  if (!summary || summary.totalDays === 0) return null;
  const pct = summary.percentage;
  const color = pct >= 75 ? '#16a34a' : pct >= 50 ? '#d97706' : '#dc2626';
  return (
    <span style={{ fontSize: 12, color, fontWeight: 600, marginLeft: 8 }}>
      📅 {summary.presentDays}/{summary.totalDays} days ({pct}%)
    </span>
  );
}

function MarksheetPreview({ marksheet }) {
  if (!marksheet || !marksheet.exams) return (
    <p style={{ color: '#6b7280', fontSize: 13, margin: '8px 0 0' }}>No marksheet linked for this year.</p>
  );

  const exams = marksheet.exams;
  const examKeys = Object.keys(exams).filter(k => {
    const val = exams[k];
    return val && typeof val === 'object' && Object.keys(val).length > 0;
  });

  if (examKeys.length === 0) return (
    <p style={{ color: '#6b7280', fontSize: 13, margin: '8px 0 0' }}>Marksheet exists but has no exam data.</p>
  );

  const subjects = Object.keys(exams[examKeys[0]] || {});

  return (
    <div style={{ overflowX: 'auto', marginTop: 10 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#1e40af', color: '#fff' }}>
            <th style={{ padding: '6px 10px', textAlign: 'left', borderRadius: '4px 0 0 0' }}>Subject</th>
            {examKeys.map(k => (
              <th key={k} style={{ padding: '6px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                {k.replace(/([A-Z])/g, ' $1').trim()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {subjects.map((sub, i) => (
            <tr key={sub} style={{ background: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
              <td style={{ padding: '5px 10px', fontWeight: 600, color: '#1e3a8a' }}>{sub}</td>
              {examKeys.map(k => (
                <td key={k} style={{ padding: '5px 8px', textAlign: 'center', color: '#374151' }}>
                  {exams[k]?.[sub] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AcademicHistoryTimeline({ studentId, showOnlyPrevious = false }) {
  const [records, setRecords]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [expanded, setExpanded]   = useState(null);
  const [error, setError]         = useState('');

  useEffect(() => {
    if (!studentId) return;
    axios.get(`${API_BASE_URL}/api/academic-history/${studentId}`)
      .then(r => setRecords(r.data))
      .catch(() => setError('Could not load academic history.'))
      .finally(() => setLoading(false));
  }, [studentId]);

  const displayRecords = showOnlyPrevious
    ? records.filter(r => r.status !== 'current')
    : records;

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: '#1e40af', fontWeight: 600 }}>
      ⏳ Loading academic history…
    </div>
  );

  if (error) return (
    <div style={{ padding: 16, background: '#fee2e2', color: '#dc2626', borderRadius: 8 }}>{error}</div>
  );

  if (displayRecords.length === 0) return (
    <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>📚</div>
      <p style={{ fontWeight: 600 }}>
        {showOnlyPrevious ? 'No previous year results found.' : 'No academic history found.'}
      </p>
      <p style={{ fontSize: 13 }}>
        {showOnlyPrevious
          ? 'Previous year results will appear here after promotion.'
          : 'History will appear here after the migration is run or after the first promotion.'}
      </p>
    </div>
  );

  return (
    <div style={{ padding: '8px 0' }}>
      {!showOnlyPrevious && (
        <h3 style={{ color: '#1e40af', marginBottom: 20, fontSize: 18 }}>📖 Academic History</h3>
      )}

      {displayRecords.map(rec => {
        const cfg = STATUS_CONFIG[rec.status] || STATUS_CONFIG.current;
        const isOpen = expanded === rec._id;

        return (
          <div key={rec._id} style={{ marginBottom: 12 }}>
            {/* Timeline row */}
            <div
              onClick={() => setExpanded(isOpen ? null : rec._id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                background: cfg.bg, border: `1.5px solid ${cfg.border}`,
                transition: 'box-shadow 0.2s',
                boxShadow: isOpen ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              <span style={{ fontSize: 22 }}>{cfg.emoji}</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 700, color: cfg.color, fontSize: 15 }}>
                  {rec.academicYear}
                </span>
                <span style={{ margin: '0 8px', color: '#9ca3af' }}>—</span>
                <span style={{ fontWeight: 600, color: '#1e3a8a', fontSize: 15 }}>
                  Class {rec.class}
                </span>
                <span style={{
                  marginLeft: 10, fontSize: 11, fontWeight: 700,
                  color: cfg.color, background: '#fff',
                  padding: '2px 8px', borderRadius: 20, border: `1px solid ${cfg.border}`
                }}>
                  {cfg.label}
                </span>
                {rec.migratedFromExisting && (
                  <span style={{
                    marginLeft: 6, fontSize: 10, color: '#6b7280',
                    background: '#f3f4f6', padding: '2px 6px', borderRadius: 20,
                    border: '1px solid #e5e7eb'
                  }}>
                    migrated
                  </span>
                )}
                <AttendanceBadge summary={rec.attendanceSummary} />
              </div>
              <span style={{ color: '#9ca3af', fontSize: 18 }}>{isOpen ? '▲' : '▼'}</span>
            </div>

            {/* Expanded detail */}
            {isOpen && (
              <div style={{
                border: `1.5px solid ${cfg.border}`, borderTop: 'none',
                borderRadius: '0 0 10px 10px', padding: 16,
                background: '#fff',
              }}>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 8 }}>
                  <div>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>Academic Year</span>
                    <div style={{ fontWeight: 700, color: '#1e3a8a' }}>{rec.academicYear}</div>
                  </div>
                  <div>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>Class</span>
                    <div style={{ fontWeight: 700, color: '#1e3a8a' }}>{rec.class}</div>
                  </div>
                  <div>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>Status</span>
                    <div style={{ fontWeight: 700, color: cfg.color }}>{cfg.emoji} {cfg.label}</div>
                  </div>
                  {rec.attendanceSummary?.totalDays > 0 && (
                    <div>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>Attendance</span>
                      <div style={{ fontWeight: 700, color: '#1e3a8a' }}>
                        {rec.attendanceSummary.presentDays}/{rec.attendanceSummary.totalDays} ({rec.attendanceSummary.percentage}%)
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 4 }}>
                  <p style={{ fontWeight: 600, color: '#374151', marginBottom: 6, fontSize: 13 }}>
                    📊 Marksheet — {rec.marksheetClass || rec.class}
                  </p>
                  <MarksheetPreview marksheet={rec.marksheetId} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
